import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FileUpload } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Settings } from '../_core/settings';
import { INotificationData } from '../core.interface';
import { Spinner } from '../_core/spinner';
import { ToastService } from '../_core/toast.service';
import { Core } from '../_core/core';

@Component({
  selector: 'app-send-notification',
  imports: [
    ButtonModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    FileUpload,
    FloatLabelModule,
    MessageModule,
    SelectModule,
    TextareaModule,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './send-notification.html',
  styleUrl: './send-notification.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendNotification implements OnInit {
  readonly title = inject(Title).setTitle('Oficina Virtual - Enviar consulta');
  private formBuilder = inject(FormBuilder);
  private $settings = inject(Settings);
  private $toast = inject(ToastService);
  protected $core = inject(Core);
  private router = inject(Router);

  private $spinner = inject(Spinner);

  public form: FormGroup = this.initForm();
  public uploadedFiles: any[] = [];
  public submittedSuccessfully = signal(false);
  public reasons = [
    { option: 'Incidencia', value: 'Incidencia' },
    { option: 'Lectura', value: 'Lectura' },
    { option: 'Revisión', value: 'Revision' },
    { option: 'Otros', value: 'Otros' },
  ];

  private readonly syncContactFields = effect(() => {
    const tenantEmail = this.$settings.tenant()?.tenantEmail;

    if (tenantEmail && !this.form.get('contactInfo')?.value) {
      this.form.patchValue({ contactInfo: tenantEmail }, { emitEvent: false });
    }
  });

  private initForm(): FormGroup {
    return this.formBuilder.group({
      contactInfo: [null, Validators.compose([Validators.required, Validators.maxLength(250)])],
      reason: [null, Validators.required],
      message: [null, Validators.compose([Validators.required, Validators.maxLength(2048)])],
      attachment: [null],
    });
  }

  async ngOnInit(): Promise<void> {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (tenantId) {
      await this.$settings.fetchTenantMe(tenantId);
    }
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const files: FileList | null = this.form.get('attachment')?.value ?? null;
    const DATA: INotificationData = {
      contactInfo: this.form.value.contactInfo,
      reason: this.form.value.reason,
      message: this.form.value.message,
      attachments: files ? Array.from(files) : undefined,
    };

    void this.sendNotification(DATA);
  }

  async sendNotification(data: INotificationData): Promise<void> {
    this.$spinner.show();
    try {
      const isSended = await this.$settings.sendNotification(
        data,
        this.$core.selectedBuilding()!.tenantId,
      );
      if (isSended) {
        this.submittedSuccessfully.set(true);
      } else {
        this.$toast.error('Ha ocurrido un error al procesar su petición');
      }
      this.$spinner.hide();
    } catch {
      this.$spinner.hide();
      this.$toast.error('Ha ocurrido un error al procesar su petición');
    }
  }

  goToHome(): void {
    void this.router.navigate(['/home']);
  }

  // FILE UPLOAD
  public onUpload(event: any): void {
    const files: FileList = event.currentFiles;
    if (files.length > 0) {
      this.form.patchValue({ attachment: files });
      this.form.get('attachment')!.updateValueAndValidity();
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result as string;
        // Si quieres solo el contenido base64 sin encabezado:
        const base64 = result.split(',')[1];
        resolve(base64);
      };

      reader.onerror = (error) => reject(error);

      reader.readAsDataURL(file);
    });
  }

  public isImage(file: any): boolean {
    return file.type?.startsWith('image/');
  }

  public isPdf(file: any): boolean {
    return file.type === 'application/pdf';
  }

  public removeFile(file: File, index: number, fileUpload: any): void {
    fileUpload.files.splice(index, 1);
    fileUpload.onRemove.emit({ file });

    const currentFiles: FileList | null = this.form.get('attachment')?.value;

    if (!currentFiles) {
      this.form.patchValue({ attachment: null });
      return;
    }

    const filesArray: File[] = Array.from(currentFiles).filter((f) => f !== file);

    const dataTransfer = new DataTransfer();
    filesArray.forEach((f) => dataTransfer.items.add(f));

    /** 5. Actualizar el form */
    this.form.patchValue({
      attachment: dataTransfer.files.length ? dataTransfer.files : null,
    });

    this.form.get('attachment')!.updateValueAndValidity();
  }

  public clearUpload(event: any): void {
    // this.fileSelected = null;
    this.uploadedFiles = [];
  }
}
